// src/lib/guided/resultScreen.ts
//
// Plan 5.1 of docs/guided-shopping_plan.md: what the result screen shows and
// in what order (research §3.5 item 2, §3.4 table), and whether it needs a
// contact step at all. Pure and framework-agnostic on purpose -- both
// `LiveForm.tsx` and the Preact embed (`kiroku-embed.ts`) call
// `buildResultScreen` for the same answers and get the same content, the
// same "one evaluator, many renderers" discipline `tallyOutcomes`
// (src/lib/quiz/outcomes.ts) already established for the outcome quiz.
//
// Order, per research: recap, reframe, consequence, the visitor's own
// outcome pick echoed back, the recommendation, the hesitation answer, next
// steps, one action. Missing owner copy (reframe, consequence, hesitation)
// drops its block -- nothing here invents copy the owner never wrote.
import type { FunnelConfig, FunnelCriterion, FunnelOffer } from "./config";
import type { FormOutcome } from "@/lib/types";
import {
  NOT_SURE_YET_OPTION,
  PREFER_TO_DISCUSS_OPTION,
  buildNeedCriterion,
} from "./compile";
import { explainOutcome, type ExplainResult } from "./explain";
import { resolveOutcome } from "./outcomes";
import { gradeLead } from "./grade";

export interface ResultScreenAnswer {
  question: string;
  answer: string;
}

export interface ResultScreenConsequence {
  text: string;
  source: string;
}

export interface ResultScreen {
  outcome: FormOutcome;
  /** "Here's what you told us": every question the visitor actually answered. */
  recap: ResultScreenAnswer[];
  /** Challenger's "teach", owner-written. Null when the owner wrote none. */
  reframe: string | null;
  /** SPIN's implication. Null unless the owner also credited a source. */
  consequence: ResultScreenConsequence | null;
  /** SPIN's need-payoff, echoed back. Null when the config has no such question. */
  outcomePick: string | null;
  /** MI's summary plus the benefit-first "because you said X" explanation (3.3). */
  recommendation: ExplainResult;
  /** The owner's own answer to whichever hesitation the visitor raised. */
  hesitationAnswer: string | null;
  /** Three steps, sized to the offer's action. Empty for the polite-no outcome. */
  nextSteps: string[];
  /** Only "quote" offers ask for contact details -- book/guide/refer already link out. */
  needsContact: boolean;
  contactPrompt: string | null;
  /** Who receives the data, said plainly (FTC 2022, research §3.11). */
  recipientLine: string | null;
  /**
   * "One action sized to the grade": the contact step's own button label,
   * varied by urgency -- never the grade letter or reasons themselves,
   * which stay owner-only (3.6, tests/guard/guided-grade-never-reaches-
   * visitor.test.ts). Null whenever there is no contact step to label.
   */
  actionLabel: string | null;
  /**
   * Plan 5.7: "Email me this summary" (Gartner's consensus-creation buying
   * job, research §2.6 item 4). Read straight off `config.shareSummary`
   * (today set only by the b2b-software-demo template), independent of
   * `needsContact` -- the B2B seed's own offer is `book`, which needs no
   * contact step, so this is the visitor's only voluntary way to keep a
   * copy of what they answered.
   */
  offersSummaryEmail: boolean;
}

function isHedgeValue(value: string): boolean {
  return (
    value === String(NOT_SURE_YET_OPTION.value) ||
    value === String(PREFER_TO_DISCUSS_OPTION.value)
  );
}

function answeredValue(
  answers: Record<string, unknown>,
  criterionId: string,
): string | null {
  const value = answers[criterionId];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * Unlike `explain.ts`'s own `bucketLabel`, this also resolves the two
 * synthetic hedge options (compile.ts's `optionsForCriterion`), which are
 * never part of `criterion.buckets` itself: the recap shows a hedge answer
 * too (the visitor did tell us that), just with its proper label rather
 * than the raw "not-sure-yet" value.
 */
function labelForAnswer(criterion: FunnelCriterion, value: string): string {
  if (value === String(NOT_SURE_YET_OPTION.value)) {
    return NOT_SURE_YET_OPTION.label;
  }
  if (value === String(PREFER_TO_DISCUSS_OPTION.value)) {
    return PREFER_TO_DISCUSS_OPTION.label;
  }
  return (
    criterion.buckets.find((bucket) => bucket.id === value)?.label ?? value
  );
}

/** Every criterion the visitor actually answered, in question order. */
function buildRecap(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): ResultScreenAnswer[] {
  const criteria: FunnelCriterion[] = [
    ...(config.offers.length > 1 ? [buildNeedCriterion(config.offers)] : []),
    ...config.criteria,
  ];
  const recap: ResultScreenAnswer[] = [];
  for (const criterion of criteria) {
    const value = answeredValue(answers, criterion.id);
    if (value === null) continue;
    recap.push({
      question: criterion.question,
      answer: labelForAnswer(criterion, value),
    });
  }
  return recap;
}

/** SPIN's need-payoff ("if this were fixed, what would matter most?"), echoed back. */
function buildOutcomePick(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): string | null {
  const criterion = config.criteria.find((c) => c.type === "outcomePriority");
  if (!criterion) return null;
  const value = answeredValue(answers, criterion.id);
  if (value === null || isHedgeValue(value)) return null;
  return `What matters most to you: ${labelForAnswer(criterion, value)}.`;
}

/**
 * "hesitation" buckets map to the owner's written question answers by id
 * (plan 1.1/1.4): the criterion's chosen bucket id is looked up directly
 * against `config.concerns`, rather than a second mapping that could drift
 * from the one `HandoffSection`/the dashboard already assumes.
 */
function buildHesitationAnswer(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): string | null {
  const criterion = config.criteria.find((c) => c.type === "hesitation");
  if (!criterion) return null;
  const value = answeredValue(answers, criterion.id);
  if (value === null || isHedgeValue(value)) return null;
  const concern = config.concerns.find((c) => c.id === value);
  return concern ? concern.answer : null;
}

/**
 * Solution Selling's "here's how this would work for you" vision (research
 * §3.4 table), sized to the offer's own action rather than invented per
 * offer -- there is no owner-authored field for this, and the four action
 * types are a closed set (config.ts's `OFFER_ACTION_TYPES`). Empty for the
 * polite-no outcome: a decline has no next step to walk through.
 */
const NEXT_STEPS_BY_ACTION: Record<FunnelOffer["action"]["type"], string[]> = {
  book: ["Pick a time that works for you", "We confirm the details", "We meet"],
  quote: [
    "Tell us where to send it",
    "We look at your answers",
    "You get your quote",
  ],
  guide: [
    "Open the guide",
    "Look through it whenever suits you",
    "Get in touch if you have questions",
  ],
  refer: [
    "Follow the link",
    "Get set up with them",
    "Come back if it's not the right fit",
  ],
};

function buildNextSteps(offer: FunnelOffer | undefined): string[] {
  return offer ? NEXT_STEPS_BY_ACTION[offer.action.type] : [];
}

/**
 * Plan 5.1: "the contact step is worded as the thing that makes the outcome
 * happen." Only a `quote` offer has nowhere else to send the visitor
 * (`outcomes.ts`'s `ctaForOfferAction`: book/guide/refer each carry their
 * own link already), so only `quote` asks for anything here.
 */
const CONTACT_PROMPT = "Where should we send your quote?";

/**
 * Plan 5.1: "the contact step says who receives the data" (FTC 2022,
 * research §3.11: gating a result behind contact details is itself a listed
 * dark pattern, which is why the recommendation above always renders
 * first). Names the form itself -- the only business-identifying string
 * either renderer has on hand -- rather than a generic "us".
 */
function buildRecipientLine(formName: string): string {
  return `This goes straight to ${formName}, so they can put your quote together. It won't be shared or sold.`;
}

/**
 * "One action sized to the grade" (research §3.5 item 2/3): the button
 * copy leans urgent for a hot, ready-now lead and stays low-pressure
 * otherwise, without ever showing the letter or the reasons behind it --
 * those are `gradeLead`'s own owner-only output (3.6). A visitor sees a
 * different sentence, never a grade.
 */
function buildActionLabel(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): string {
  const { grade, nurture } = gradeLead(config, answers);
  return grade === "A" && !nurture ? "Get my quote now" : "Get my quote";
}

/**
 * The result screen for a submitted (or in-progress) set of answers:
 * `resolveOutcome`'s outcome plus every block research §3.5 item 2 asks
 * for, in order. Pure: the same `config`/`answers` always produce the same
 * screen, so a renderer can call this on every keystroke without caching.
 */
export function buildResultScreen(
  config: FunnelConfig,
  answers: Record<string, unknown>,
  formName: string,
): ResultScreen {
  const outcome = resolveOutcome(config, answers);
  const offer = config.offers.find((o) => o.id === outcome.id);
  const needsContact = offer?.action.type === "quote";
  const reframe = offer?.reframe?.trim();

  return {
    outcome,
    recap: buildRecap(config, answers),
    reframe: reframe ? reframe : null,
    consequence: offer?.consequence ?? null,
    outcomePick: buildOutcomePick(config, answers),
    recommendation: explainOutcome(config, answers),
    hesitationAnswer: buildHesitationAnswer(config, answers),
    nextSteps: buildNextSteps(offer),
    needsContact,
    contactPrompt: needsContact ? CONTACT_PROMPT : null,
    recipientLine: needsContact ? buildRecipientLine(formName) : null,
    actionLabel: needsContact ? buildActionLabel(config, answers) : null,
    offersSummaryEmail: config.shareSummary === true,
  };
}
